import type { Listing } from './layouts';

/**
 * The stamp card (E17): one stamp per Labs tool a visitor has got a result from, plus two
 * for coming back and for trying three tools.
 *
 * Pure. The Worker reads the facts from PostHog; this decides what the strip shows. A stamp
 * is earned on a tool's RESULT, never on opening it — opening is what the card is trying to
 * cause, so counting it would let the card reward itself.
 */
export type StampCardVariant = 'control' | 'stamps';

/**
 * Each tool's result events, by listing id. The Worker builds its query from this map, so
 * the stamp and the query cannot disagree about what counts.
 *
 * 240 Sites is absent on purpose: its Worker sends anonymous pageviews with no person, so no
 * event ties a visitor to a Sites result.
 */
export const RESULT_EVENTS: Readonly<Record<string, readonly string[]>> = {
  one_step_ahead: ['preview_osa_report_viewed'],
  objectives: ['preview_objectives_run_completed', 'preview_objectives_report_viewed', 'objectives_report_viewed'],
  rubric: ['preview_rubric_scored'],
  collab: ['preview_collab_material_shared'],
  planner: ['preview_planner_plan_built'],
  profile: ['preview_profile_entry_added'],
  jobs: ['jobs_posting_opened'],
  writing_feedback: ['writing_feedback_checked'],
  ai_detector: ['ai_detector_checked'],
};

export const CAME_BACK = 'came_back';
export const THREE_TOOLS = 'three_tools';

/** What the Worker returns: which tools have a result, and on how many distinct UTC days. */
export interface StampFacts {
  tools: Partial<Record<string, boolean>>;
  days: number;
}

export interface Stamp {
  id: string;
  label: string;
  /** How to earn it, in the visitor's words. */
  hint: string;
  filled: boolean;
  /** Tool stamps only. */
  tool?: string;
  href?: string;
}

/**
 * Tool stamps for open, mapped listings, in listing order, then the two bonus stamps. A
 * hidden tool gets no stamp and cannot count towards three, so the card never names a tool
 * its listing flag hides.
 */
export function deriveStamps(facts: StampFacts, listings: readonly Listing[]): Stamp[] {
  const toolStamps: Stamp[] = listings
    .filter(l => l.status === 'open' && l.entry.id in RESULT_EVENTS)
    .map(l => ({
      id: l.entry.id,
      label: l.entry.name,
      hint: `Finish something in ${l.entry.name}`,
      filled: facts.tools[l.entry.id] === true,
      tool: l.entry.id,
      href: l.href,
    }));
  const filled = toolStamps.filter(s => s.filled).length;
  return [
    ...toolStamps,
    { id: CAME_BACK, label: 'Came back', hint: 'Use a tool on two different days', filled: facts.days >= 2 },
    { id: THREE_TOOLS, label: 'Three tools tried', hint: 'Earn three tool stamps', filled: filled >= 3 },
  ];
}

/** Filled stamps this visitor has not been shown filled before. */
export function newlyFilled(stamps: readonly Stamp[], seen: ReadonlySet<string>): Stamp[] {
  return stamps.filter(s => s.filled && !seen.has(s.id));
}

/** A Clerk user id, the only shape the Worker will put into a query. */
export const CLERK_USER_ID = /^user_[A-Za-z0-9]{1,64}$/;

/** True for a body the Worker could have produced; anything else is treated as no card. */
export function isStampFacts(value: unknown): value is StampFacts {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as { tools?: unknown; days?: unknown };
  return typeof v.tools === 'object' && v.tools !== null && !Array.isArray(v.tools)
    && Object.values(v.tools).every(b => typeof b === 'boolean')
    && typeof v.days === 'number' && Number.isInteger(v.days) && v.days >= 0;
}

/** True when the visitor arrived from a Labs tool, so the page should ask for a fresh read. */
export function cameFromTool(referrer: string): boolean {
  try {
    const host = new URL(referrer).hostname;
    return host.endsWith('.labs.240.org');
  } catch { return false; }
}
